import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export default async function generateDistrictDisposeExcel(
    data,
    startDate,
    endDate,
    selectedDistrictNames = [],
    loginPlaceId,
    productHeader = "All Products"
) {
    if (!Array.isArray(data) || data.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Dispose Summary');

    const totalColumns = 6;

    // === TITLE SECTION ===
    sheet.mergeCells(1, 1, 1, totalColumns);
    const productTitle = sheet.getCell('A1');
    productTitle.value = `Product: ${productHeader}`;
    productTitle.font = { bold: true, size: 18, color: { argb: '000080' } };
    productTitle.alignment = { horizontal: 'center', vertical: 'middle' };
    productTitle.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DDEBF7' } };
    sheet.getRow(1).height = 50;

    sheet.mergeCells(2, 1, 2, totalColumns);
    const mainTitle = sheet.getCell('A2');
    mainTitle.value = 'Loan Dispose Summary Report (District & Branch Wise)';
    mainTitle.font = { bold: true, size: 14 };
    mainTitle.alignment = { horizontal: 'center' };
    sheet.getRow(2).height = 35;

    sheet.mergeCells(3, 1, 3, totalColumns);
    const periodCell = sheet.getCell('A3');
    periodCell.value = `Period: ${startDate} to ${endDate}`;
    periodCell.font = { italic: true, size: 11 };
    periodCell.alignment = { horizontal: 'center' };
    sheet.getRow(3).height = 25;

    sheet.addRow([]);

    // ====================================================
    // === DISTRICT SUMMARY TABLE =========================
    // ====================================================

    if (loginPlaceId !== "1" && loginPlaceId !== "2") {

        const summaryTitleRow = sheet.addRow(['District Summary']);
        sheet.mergeCells(summaryTitleRow.number, 1, summaryTitleRow.number, 3);
        summaryTitleRow.font = { bold: true, size: 12 };

        const summaryHeader = sheet.addRow([
            'District',
            'Total Count',
            'Total Amount'
        ]);

        summaryHeader.eachCell(cell => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BDD7EE' } };
            cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
        });

        const summaryStartRow = sheet.lastRow.number + 1;

        data.forEach(district => {
            let districtCount = 0;
            let districtAmount = 0;

            district.loanSummaryWithDivisionalDtoList.forEach(divisional => {
                divisional.loanSummaryWithBranchDtoList.forEach(branch => {
                    branch.loanSummaryDtoList.forEach(loan => {
                        districtCount += loan.disbursementCount || 0;
                        districtAmount += loan.disbursementAmount || 0;
                    });
                });
            });

            const row = sheet.addRow([
                district.districtName,
                districtCount,
                districtAmount
            ]);

            row.eachCell((cell, colNum) => {
                cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
                if (colNum === 2) cell.numFmt = '#,##0';
                if (colNum === 3) cell.numFmt = '#,##0.00';
            });
        });

        const summaryEndRow = sheet.lastRow.number;

        const summaryTotalRow = sheet.addRow([
            'TOTAL',
            { formula: `SUM(B${summaryStartRow}:B${summaryEndRow})` },
            { formula: `SUM(C${summaryStartRow}:C${summaryEndRow})` }
        ]);

        summaryTotalRow.eachCell((cell, colNum) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E2EFDA' } };
            cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
            if (colNum === 2) cell.numFmt = '#,##0';
            if (colNum === 3) cell.numFmt = '#,##0.00';
        });

        sheet.addRow([]);
        sheet.addRow([]);
    }

    if (loginPlaceId !== "1") {
        const divTitleRow = sheet.addRow(['Divisional Summary']);
        sheet.mergeCells(divTitleRow.number, 1, divTitleRow.number, 3);
        divTitleRow.font = { bold: true, size: 12 };

        const divHeader = sheet.addRow([
            'District',
            'Divisional',
            'Total Count',
            'Total Amount'
        ]);

        divHeader.eachCell(cell => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FCE4D6' } };
            cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
        });

        const divStartRow = sheet.lastRow.number + 1;

        // === Collect divisional totals ===
        // === Collect divisional totals WITH district ===
        const divisionalMap = new Map();

        data.forEach(district => {
            const districtName = district.districtName;

            district.loanSummaryWithDivisionalDtoList.forEach(divisional => {
                let count = 0;
                let amount = 0;

                divisional.loanSummaryWithBranchDtoList.forEach(branch => {
                    branch.loanSummaryDtoList.forEach(loan => {
                        count += loan.disbursementCount || 0;
                        amount += loan.disbursementAmount || 0;
                    });
                });

                // composite key: district + divisional
                const key = `${districtName}__${divisional.divisionalName}`;

                if (!divisionalMap.has(key)) {
                    divisionalMap.set(key, {
                        district: districtName,
                        divisional: divisional.divisionalName,
                        count: 0,
                        amount: 0
                    });
                }

                const entry = divisionalMap.get(key);
                entry.count += count;
                entry.amount += amount;
            });
        });

        // === Render divisional rows ===
        divisionalMap.forEach(val => {
            const row = sheet.addRow([
                val.district,
                val.divisional,
                val.count,
                val.amount
            ]);

            row.eachCell((cell, colNum) => {
                cell.border = {
                    top: { style: 'thin' },
                    bottom: { style: 'thin' },
                    left: { style: 'thin' },
                    right: { style: 'thin' }
                };

                if (colNum === 3) cell.numFmt = '#,##0';
                if (colNum === 4) cell.numFmt = '#,##0.00';
            });
        });


        const divEndRow = sheet.lastRow.number;

        const divTotalRow = sheet.addRow([
            'TOTAL', '',
            { formula: `SUM(C${divStartRow}:C${divEndRow})` },
            { formula: `SUM(D${divStartRow}:D${divEndRow})` }
        ]);

        divTotalRow.eachCell((cell, colNum) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2CC' } };
            cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
            if (colNum === 2) cell.numFmt = '#,##0';
            if (colNum === 3) cell.numFmt = '#,##0.00';
        });

        sheet.addRow([]);
        sheet.addRow([]);
    }


    // ====================================================
    // === MAIN TABLE HEADERS =============================
    // ====================================================

    const headers = [
        'District Name', 'Divisional Name', 'Branch Name', 'Product Name',
        'Disbursement Count', 'Disbursement Amount'
    ];

    const headerRow = sheet.addRow(headers);
    headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4472C4' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
    });

    sheet.views = [{ state: 'frozen', ySplit: 3 }];

    let currentRow = sheet.lastRow.number + 1;
    let zebraIndex = 0;
    const grandTotalRows = [];

    data.forEach((district) => {
        const districtTotalRows = [];

        district.loanSummaryWithDivisionalDtoList.forEach((divisional) => {
            const divisionalTotalRows = [];

            divisional.loanSummaryWithBranchDtoList.forEach((branch) => {
                const startProductRow = currentRow;

                branch.loanSummaryDtoList.forEach((loan) => {
                    const row = sheet.addRow([
                        district.districtName,
                        divisional.divisionalName,
                        branch.branchName,
                        loan.productName,
                        loan.disbursementCount,
                        loan.disbursementAmount,
                    ]);

                    applyDataRowStyling(row, zebraIndex++);
                    currentRow++;
                });

                const endProductRow = currentRow - 1;

                const branchSubRow = sheet.addRow([
                    '', '', `${branch.branchName} TOTAL`, '',
                    { formula: `SUM(E${startProductRow}:E${endProductRow})` },
                    { formula: `SUM(F${startProductRow}:F${endProductRow})` },
                ]);

                applySubtotalStyling(branchSubRow, 'F8F9FA');
                divisionalTotalRows.push(currentRow);
                currentRow++;
            });

            const divisionalSubRow = sheet.addRow([
                '', `${divisional.divisionalName} TOTAL`, '', '',
                { formula: divisionalTotalRows.map(r => `E${r}`).join('+') },
                { formula: divisionalTotalRows.map(r => `F${r}`).join('+') },
            ]);

            applySubtotalStyling(divisionalSubRow, 'DDEBF7');
            districtTotalRows.push(currentRow);
            grandTotalRows.push(currentRow);
            currentRow++;
        });

        const districtSubRow = sheet.addRow([
            `${district.districtName} TOTAL`, '', '', '',
            { formula: districtTotalRows.map(r => `E${r}`).join('+') },
            { formula: districtTotalRows.map(r => `F${r}`).join('+') },
        ]);

        applySubtotalStyling(districtSubRow, 'E2EFDA');
        currentRow++;
    });

    const grandTotalRow = sheet.addRow([
        'GRAND TOTAL', '', '', '',
        { formula: grandTotalRows.map(r => `E${r}`).join('+') },
        { formula: grandTotalRows.map(r => `F${r}`).join('+') },
    ]);

    applySubtotalStyling(grandTotalRow, 'DDEBF7');
    grandTotalRow.getCell(1).font = { bold: true, size: 12 };

    // === COLUMN WIDTHS & FREEZE ===
    sheet.columns = [
        { width: 25 }, { width: 30 }, { width: 30 },
        { width: 45 }, { width: 18 }, { width: 22 }
    ];


    // === EXPORT ===
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Summary_${startDate}_to_${endDate}.xlsx`);
}

function applyDataRowStyling(row, zebraIndex) {
    const zebraColor = zebraIndex % 2 === 0 ? 'FFFFFF' : 'F2F2F2';

    row.eachCell((cell, colNum) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: zebraColor } };
        cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };

        if (colNum === 5) cell.numFmt = '#,##0';
        if (colNum === 6) cell.numFmt = '#,##0.00';
    });
}

function applySubtotalStyling(row, color) {
    row.eachCell((cell, colNum) => {
        cell.font = { bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
        cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };

        if (colNum === 5) cell.numFmt = '#,##0';
        if (colNum === 6) cell.numFmt = '#,##0.00';
    });
}
